// react
import { useMemo } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

// components
import { Link, NavVertical } from '@/components/molecules';

// hooks/
import { useWindowSize } from '@/hooks/';

// theme
import theme from '@/theme';
import { Icon } from '@/components/atoms';

/**
 * NavDrawer is a navigation drawer that slides in from the left side of the
 * screen.
 */
export const NavDrawer = ({
  className,
  imgSrc,
  fallback,
  name,
  role,
  links,
  currentLink,
  isCollapsed: isCollapsedProp,
  onToggleCollapse,
  isHideSummary,
}: NavDrawerProps) => {
  // hooks/
  const windowSize = useWindowSize();

  // memo
  const isMobile = useMemo(() => {
    return windowSize[0] <= theme.breakpoint.mdValue;
  }, [windowSize]);

  const isCollapsed = useMemo(() => {
    return isMobile || isCollapsedProp;
  }, [isCollapsedProp, isMobile]);

  return (
    <StyledNavDrawer className={className} $isCollapsed={isCollapsed}>
      <Header>
        {!isMobile && (
          <CollapseContainer $isCollapsed={isCollapsed}>
            <CollapseButton onClick={() => onToggleCollapse?.()}>
              <Icon icon={isCollapsed ? 'chevrons-right' : 'chevrons-left'} />
            </CollapseButton>
          </CollapseContainer>
        )}
      </Header>
      <NavVertical
        imgSrc={imgSrc}
        fallback={fallback}
        name={name}
        role={role}
        links={links}
        currentLink={currentLink}
        isCollapsed={isCollapsed}
        isHideSummary={isHideSummary}
      />
    </StyledNavDrawer>
  );
};

interface StyledNavDrawerProps {
  $isCollapsed?: boolean;
}

const StyledNavDrawer = styled.div<StyledNavDrawerProps>`
  position: relative;
  overflow: hidden;
  display: inline-flex;
  flex-direction: column;
  background: ${(props) => props.theme.color.neutral[10]};
  padding: ${(props) => props.theme.spacing[48]}
    ${(props) => props.theme.spacing[16]};
  height: 100%;
  box-sizing: border-box;
  border-right: 1px solid
    ${(props) => props.theme.color.illustration.eggBlueLight};
  ${(props) =>
    !props.$isCollapsed &&
    css`
      min-width: 300px;
      width: 300px;
      padding: ${props.theme.spacing[48]} ${props.theme.spacing[24]};
    `}
`;

interface CollapseContainerProps {
  $isCollapsed?: boolean;
}

const CollapseContainer = styled.div<CollapseContainerProps>`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  padding: ${(props) => props.theme.spacing[12]};
  box-sizing: border-box;
  ${(props) =>
    props.$isCollapsed &&
    css`
      justify-content: center;
    `}
`;

const CollapseButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(props) => props.theme.color.neutral[70]};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
`;

interface NavDrawerProps {
  className?: string;
  imgSrc?: string;
  fallback: string;
  name: string;
  role: string;
  currentLink: string;
  links: Link[];
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  isHideSummary?: boolean;
}

NavDrawer.displayName = 'NavDrawer';

export default NavDrawer;
